import { gql } from '@apollo/client'

export const orderListQuery = gql`
  query orderList($filter: JSON, $options: JSON) {
    orderList(filter: $filter, options: $options) {
      _id
      code
      status
      createdAt
      updatedAt
      net
      remarks
      tags
      customerPoNumber
      by {
        _id
        profile {
          _id
          name
          picture
          email
        }
        status
        phone
        company {
          _id
          name
        }
      }
      shippingAddress {
        _id
        fullAddress
      }
      shipping {
        _id
        date
        rate {
          _id
          serviceType
        }
        trackingNumber
        packages {
          _id
          tracking_number
          trackingUrl
        }
      }
      paymentStatus
      qboId
      summary
      remarksActivity(filter: {}, options: { sort: { updatedAt: -1 }, limit: 10 }) {
        _id
        activity
        by {
          _id
          profile {
            _id
            name
            picture
          }
        }
        updatedAt
        isDeleted
      }
      items {
        _id
        quantity
        product {
          _id
          code
          name
          coverImage
          status
        }
        price
        net
      }
      paymentBalance {
        _id
        balance
      }
      payments {
        _id
        status
        date
      }
    }

    orderListMeta(filter: $filter) {
      count
    }
  }
`

export const orderListGetNetTotalQuery = gql`
  query orderListGetNetTotal($filter: JSON!) {
    orderListGetNetTotal(filter: $filter)
  }
`

export const shippingFragment = gql`
  fragment shippingFragment on Shipping {
    _id
    serviceErrors
    serviceWarnings
    voidMessage
    status
    shipmentId
    amount
    shippingCost
    insuranceCost
    confirmationCost
    markupCost
    otherCost
    date
    trackingNumber
    trackingUrl
    pdfLabel
    pngLabel
    zplLabel
    pickupId
    pickupWindowStartDate
    pickupWindowEndDate
    pickupNotes
    pickupCancelledDate
    isVoidLabel
    rate {
      _id
      deliveryDays
      estimatedDeliveryDate
      serviceType
      carrierName
      serviceCode
      carrierId
    }
    labelId
    billToCustomer
    billToAccount
    billToPostalCode
    billToCountryCode
    deliveryConfirmation
    insuredAmount
    packages {
      _id
      weight {
        unit
        value
      }
      dimensions {
        unit
        length
        width
        height
      }
      insured_value {
        currency
        amount
      }
      label_download {
        png
        pdf
      }
      tracking_number
      package_code
      trackingUrl
      packageType
    }
    formDownload
    hrefLabel
    itemQuantityErrors
  }
`

export const orderPaymentFragment = gql`
  fragment orderPaymentFragment on Payment {
    _id
    status
    date
    method
    remarks
    stripeInfo {
      _id
      paymentIntent
      paymentId
      sessionId
    }
    merchantFee
    net
    gross
    refunds {
      _id
      amount
      remarks
      date
      status
    }
    relatedOrderId
    checkNumber
    checkDate
  }
`

export const orderFragment = gql`
  fragment orderFragment on Order {
    _id
    summary
    code
    status
    updatedAt
    createdAt
    net
    by {
      _id
      profile {
        _id
        name
        picture
        email
      }
      status
      company {
        _id
        name
        domain
      }
      phone
      address {
        _id
        street1
        street2
        street3
        city
        state
        zip
        fullAddress
        country
      }
      addresses {
        _id
        street1
        street2
        street3
        city
        state
        zip
        fullAddress
        country
        status
      }
      ups {
        _id
        account
        postalCode
        countryCode
      }
    }
    items {
      _id
      product {
        _id
        code
        name
        coverImage
        coverImageThumb
        status
        inventory {
          _id
          quantityOnHand
        }
        inStock
        requiredComputedQuantityOnHand
        hasSubComponents
        productType
        requiredSerialNumber
        baseUom {
          _id
          symbol
        }
        unitOfMeasurePrecision
        orderConversionFactor
        metadata {
          _id
          key
          value
          unit
        }
      }
      quantity
      price
      net
      remarks
      tags
      discount
      originalPrice
      shippingWeight
      serialNumbers {
        _id
        code
      }
      uom {
        _id
        code
        name
        symbol
      }
    }
    shippingFee
    gross
    minimumOrderAmount
    remarks
    tags
    taxAmount
    discount
    discountPercent
    payments {
      ...orderPaymentFragment
    }
    communication {
      _id
      email {
        _id
        to
        from
        timeStamp
        content
      }
    }
    shipping {
      ...shippingFragment
    }
    shippingAddress {
      _id
      street1
      street2
      city
      state
      zip
      country
      fullAddress
      company
      street3
      name
      phone
    }
    billingAddress {
      _id
      street1
      street2
      city
      state
      zip
      country
      fullAddress
      company
      street3
      name
      phone
    }
    customerPoNumber
    isManualShippingRate
    allItemsInStock
    allItemsInStockByOnHandAndComputedQuantity
    totalWeight
    allowPrePayment
    invoiceNotes
    members {
      _id
      profile {
        _id
        name
        email
        picture
      }
      company {
        _id
        name
      }
      phone
      remarks
      tags
      address {
        _id
        fullAddress
      }
    }
    isManualTaxAmount
    manualTrackingUrl
    manualTrackingNumber
    manualShippingCarrier
    paymentStatus
    initialShippingFee
    paymentTerms
    activeConversationId
    checkoutStage
    paymentBalance {
      _id
      balance
      totalPaid
    }
    qboId
    relatedOrders {
      _id
      code
      status
      paymentStatus
      summary
      customerPoNumber
      relatedOrders {
        _id
      }
      by {
        _id
        company {
          _id
          name
        }
        profile {
          _id
          name
          picture
          email
        }
      }
    }
    assets {
      _id
      url
      largeUrl
      mediumUrl
      thumbUrl
      title
      subtitle
      type
    }
    remarksActivity {
      _id
      activity
      by {
        _id
        profile {
          _id
          name
          picture
        }
      }
      updatedAt
      isDeleted
    }
    fieldIssues {
      _id
      fieldName
      message
      type
    }
    relatedWorkOrders {
      _id
      code
      status
    }
  }

  ${shippingFragment}
  ${orderPaymentFragment}
`

export const singleOrderQuery = gql`
  ${orderFragment}
  query singleOrder($filter: JSON) {
    singleOrder(filter: $filter) {
      ...orderFragment
    }
  }
`

export const getMyOrderQuery = gql`
  ${orderFragment}
  query getMyOrder($filter: JSON) {
    getMyOrder(filter: $filter) {
      ...orderFragment
    }
  }
`

export const extractOrderAddressesQuery = gql`
  query extractOrderAddresses($input: ExtractOrderAddressesArgs!) {
    extractOrderAddresses(input: $input) {
      _id
      street1
      street2
      city
      state
      zip
      country
      fullAddress
      company
      name
      phone
    }
  }
`
